// pages/ActivateAccount.js - v3.0 Account Activation Page

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Card, Form, Button, Alert } from 'react-bootstrap';
import axios from 'axios';

const ActivateAccount = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const redirectTimeoutRef = useRef(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenError, setTokenError] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Token validation on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        setTokenError('رابط التفعيل غير صالح');
        setValidatingToken(false);
        return;
      }

      try {
        // Try to get user info from token (this validates token exists)
        // We'll make a simple GET request to check token validity
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/v1/users/validate-activation-token/${token}`
        );

        setTokenValid(true);
        setUserEmail(response.data.email || '');
        setValidatingToken(false);
      } catch (err) {
        setTokenValid(false);
        setValidatingToken(false);

        // Parse error message
        const errorMsg = err.response?.data?.error || err.response?.data?.message;
        if (errorMsg?.includes('expired')) {
          setTokenError('انتهت صلاحية رابط التفعيل');
          setUserEmail(err.response?.data?.email || '');
        } else if (errorMsg?.includes('already activated')) {
          setTokenError('تم تفعيل هذا الحساب مسبقاً');
          // Redirect to login after 2 seconds
          redirectTimeoutRef.current = setTimeout(() => {
            navigate('/login', {
              state: { message: 'حسابك مفعّل بالفعل، يمكنك تسجيل الدخول' }
            });
          }, 2000);
        } else {
          setTokenError('رابط التفعيل غير صالح أو منتهي الصلاحية');
        }
      }
    };

    validateToken();

    // Cleanup function to clear timeout on unmount
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [token, navigate]);

  // Calculate password strength
  const calculatePasswordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: '', color: '' };

    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^a-zA-Z0-9]/.test(pwd)) score += 1;

    if (score <= 2) return { score, label: 'ضعيفة', color: '#dc3545' };
    if (score <= 3) return { score, label: 'متوسطة', color: '#ffc107' };
    return { score, label: 'قوية', color: '#28a745' };
  };

  const passwordStrength = calculatePasswordStrength(password);

  // Resend activation email
  const handleResendActivation = async () => {
    if (!userEmail) {
      setError('لم يتم العثور على البريد الإلكتروني');
      return;
    }

    try {
      setResendLoading(true);
      setError(null);

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/users/resend-activation`,
        { email: userEmail }
      );

      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      // Handle network errors separately from server errors
      if (err.message === 'Network Error') {
        setError('خطأ في الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت');
      } else {
        setError(err.response?.data?.error || err.response?.data?.message || 'فشل إرسال رابط التفعيل');
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleActivate = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('كلمات المرور غير متطابقة');
      return;
    }

    if (password.length < 6) {
      setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/users/activate`,
        {
          activationToken: token,
          password
        }
      );

      setSuccess(true);
      // Clear any existing timeout before setting new one
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
      redirectTimeoutRef.current = setTimeout(() => {
        navigate('/login', {
          state: { message: 'تم تفعيل حسابك بنجاح، يمكنك تسجيل الدخول الآن' }
        });
      }, 2000);

    } catch (err) {
      console.error('Activation error:', err);

      // Improved error messages
      const errorMsg = err.response?.data?.error || err.response?.data?.message || '';

      if (errorMsg.includes('expired')) {
        setError('انتهت صلاحية رابط التفعيل. يرجى طلب رابط جديد');
      } else if (errorMsg.includes('invalid') || errorMsg.includes('Invalid')) {
        setError('رابط التفعيل غير صالح');
      } else if (errorMsg.includes('already activated')) {
        setError('تم تفعيل هذا الحساب مسبقاً');
        // Clear any existing timeout before setting new one
        if (redirectTimeoutRef.current) {
          clearTimeout(redirectTimeoutRef.current);
        }
        redirectTimeoutRef.current = setTimeout(() => {
          navigate('/login', {
            state: { message: 'حسابك مفعّل بالفعل، يمكنك تسجيل الدخول' }
          });
        }, 2000);
      } else if (err.message === 'Network Error') {
        setError('خطأ في الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت');
      } else {
        setError(errorMsg || 'فشل تفعيل الحساب. يرجى المحاولة مرة أخرى');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
      <Card style={{ width: '100%', maxWidth: '500px' }} dir="rtl">
        <Card.Body className="p-5">
          <div className="text-center mb-4">
            <h2>تفعيل الحساب</h2>
            <p className="text-muted">قم بإنشاء كلمة مرور لحسابك</p>
          </div>

          {/* Validating token loading state */}
          {validatingToken && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">جاري التحقق...</span>
              </div>
              <p className="text-muted">جاري التحقق من رابط التفعيل...</p>
            </div>
          )}

          {/* Invalid token error */}
          {!validatingToken && tokenValid === false && (
            <>
              <Alert variant="danger">
                {tokenError}
              </Alert>
              {userEmail && tokenError.includes('صلاحية') && (
                <div className="text-center">
                  {resendSuccess && (
                    <Alert variant="success" className="mb-3">
                      ✅ تم إرسال رابط التفعيل الجديد إلى بريدك الإلكتروني
                    </Alert>
                  )}
                  <Button
                    variant="outline-primary"
                    onClick={handleResendActivation}
                    disabled={resendLoading}
                    className="w-100 mb-3"
                  >
                    {resendLoading ? 'جاري الإرسال...' : '📧 إرسال رابط تفعيل جديد'}
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Valid token - show activation form */}
          {!validatingToken && tokenValid === true && (
            <>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {resendSuccess && (
                <Alert variant="success" dismissible onClose={() => setResendSuccess(false)}>
                  ✅ تم إرسال رابط التفعيل الجديد
                </Alert>
              )}

              {success ? (
                <Alert variant="success">
                  ✅ تم تفعيل حسابك بنجاح! جاري تحويلك لصفحة تسجيل الدخول...
                </Alert>
              ) : (
                <Form onSubmit={handleActivate}>
                  {/* Password field with show/hide toggle */}
                  <Form.Group className="mb-3">
                    <Form.Label>كلمة المرور</Form.Label>
                    <div style={{ position: 'relative' }}>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="أدخل كلمة المرور"
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute',
                          left: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '5px'
                        }}
                      >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>

                    {/* Password strength indicator */}
                    {password && (
                      <div className="mt-2">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small>قوة كلمة المرور:</small>
                          <small style={{ color: passwordStrength.color, fontWeight: 'bold' }}>
                            {passwordStrength.label}
                          </small>
                        </div>
                        <div style={{
                          height: '6px',
                          background: '#e0e0e0',
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${(passwordStrength.score / 5) * 100}%`,
                            height: '100%',
                            background: passwordStrength.color,
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>
                    )}

                    <Form.Text className="text-muted">
                      يجب أن تكون 6 أحرف على الأقل. يُنصح باستخدام أحرف كبيرة وصغيرة وأرقام
                    </Form.Text>
                  </Form.Group>

                  {/* Confirm password field with show/hide toggle */}
                  <Form.Group className="mb-4">
                    <Form.Label>تأكيد كلمة المرور</Form.Label>
                    <div style={{ position: 'relative' }}>
                      <Form.Control
                        type={showConfirmPassword ? "text" : "password"}
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="أعد إدخال كلمة المرور"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: 'absolute',
                          left: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '5px'
                        }}
                      >
                        {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    className="w-100"
                    disabled={loading}
                  >
                    {loading ? 'جاري التفعيل...' : 'تفعيل الحساب'}
                  </Button>
                </Form>
              )}
            </>
          )}

          <div className="text-center mt-4">
            <Link to="/login" className="text-decoration-none">
              العودة لتسجيل الدخول
            </Link>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ActivateAccount;
